import React, { useState } from 'react';
import { scheduleService } from '../services/scheduleService';
import { apiService } from '../services/api';
import TimezoneSelector from './schedules/TimezoneSelector';
import HostVoicePicker from './schedules/HostVoicePicker';
import Dismissible from './common/Dismissible';
import { usePlan } from '../hooks/usePlan';
import { openPricingModal } from '../utils/pricingModal';
import { getPlanLimits } from '../constants/planLimits';
import {
  POLLY_STANDARD_LANGUAGE_OPTIONS,
  getDefaultPollyVoiceForLanguage,
  getPollyLanguageName
} from '../constants/pollyStandardVoices';

const CONVERSATIONAL_LANGUAGE_MODES = [
  { value: 'secondary-only', label: 'Second language only', description: 'Hosts speak only in your learning language' },
  { value: 'native-then-secondary', label: 'English + second language', description: 'Each line in English, then your learning language' },
  { value: 'native-only', label: 'English only', description: 'Hosts speak only in English' }
];

const CONVERSATIONAL_DIFFICULTIES = [
  { value: 'easy', label: 'Easy', description: 'Beginner (A2) — simple everyday words, short sentences' },
  { value: 'medium', label: 'Medium', description: 'Intermediate (B1–B2) — the standard style' },
  { value: 'hard', label: 'Hard', description: 'Advanced (C1) — natural native-level vocabulary' }
];

const needsTranslationLanguage = (mode) => mode === 'secondary-only' || mode === 'native-then-secondary';

function planLabel(plan) {
  if (plan === 'free') return 'Free plan';
  if (plan === 'plus') return 'Plus plan';
  if (plan === 'comp') return 'Comp plan';
  return 'Your plan';
}

// existingEnabledRuns: sum of refreshTimes.length across the user's other active/non-deleted
// schedules (computed by ScheduleManager, which already has the full schedule list loaded).
function ScheduleWizard({ user, onComplete, onCancel, existingEnabledRuns = 0 }) {
  const { plan, resolved } = usePlan();
  const isFreePlan = plan === 'free';
  const planLimits = getPlanLimits(plan);
  // Articles-per-episode lock (Topic 5): only restrict once the plan has resolved from the
  // server (usePlan defaults to 'plus' while loading/on error, so isFreePlan is false during
  // that window regardless) — backend validation remains the real enforcement.
  const articlesLocked = resolved && isFreePlan;
  // Only restrict once the plan has resolved from the server (usePlan defaults to 'plus'
  // while loading/on error) — backend validation remains the real enforcement regardless.
  const remainingDailyRuns = resolved ? Math.max(0, planLimits.maxDailyRuns - existingEnabledRuns) : planLimits.maxDailyRuns;
  const dailyRunLimitReached = resolved && remainingDailyRuns <= 0;
  const maxAddableRefreshTimes = Math.min(planLimits.maxRefreshTimes, resolved ? remainingDailyRuns : planLimits.maxRefreshTimes);
  const dailyRunsMessage = plan === 'free'
    ? 'Free plan: 1 schedule with 1 episode per day. Upgrade to schedule more episodes or multiple refresh times.'
    : `${planLabel(plan)}: ${planLimits.maxDailyRuns} scheduled episode${planLimits.maxDailyRuns === 1 ? '' : 's'} per day. Upgrade to schedule more episodes or multiple refresh times.`;
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userSources, setUserSources] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    scheduleName: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    refreshTimes: [
      { localTime: '08:00', enabled: true }
    ],
    newsSources: [],
    audioSettings: {
      audioSpeed: '1.0',
      includeIntro: true,
      includeOutro: true
    },
    // New schedules default to the conversational format in bilingual mode (owner decision,
    // 2026-08-16). Switching to article still clears the mode; switching back starts unselected.
    episodeFormat: 'conversational',
    conversationalLanguageMode: 'native-then-secondary',
    conversationalDifficulty: null,
    hostVoices: null,
    articlesPerEpisode: 3
  });

  // Load user's saved news sources
  React.useEffect(() => {
    const loadSources = async () => {
      const readLocal = () => {
        try {
          const raw = localStorage.getItem(`sources_${user.username}`);
          return raw ? JSON.parse(raw) : [];
        } catch (e) {
          return [];
        }
      };

      try {
        const sources = await apiService.getUserSources(user.username);
        // Fall back to any locally-saved sources if the API returns none, so the
        // picker matches the News Sources tab instead of showing an empty state.
        setUserSources(sources && sources.length > 0 ? sources : readLocal());
      } catch (error) {
        console.error('Failed to load sources:', error);
        setUserSources(readLocal());
      }
    };
    loadSources();
  }, [user.username]);

  // Whether the currently-configured refresh times would push the user's total daily run
  // opportunities (across all their schedules) past their plan's limit. Backend re-validates
  // this on submit regardless — this is just clear, early UI feedback.
  const wouldExceedDailyRuns = resolved && (existingEnabledRuns + formData.refreshTimes.length) > planLimits.maxDailyRuns;

  const handleSubmit = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate
      if (!formData.scheduleName.trim()) {
        throw new Error('Please enter a schedule name');
      }
      if (formData.newsSources.length === 0) {
        throw new Error('Please select a news source');
      }
      if (!formData.refreshTimes.some(t => t.enabled)) {
        throw new Error('Please enable at least one refresh time');
      }
      if (wouldExceedDailyRuns) {
        throw new Error(dailyRunsMessage);
      }
      if (formData.episodeFormat === 'conversational') {
        if (!formData.conversationalLanguageMode) {
          throw new Error('Please select a language mode for the podcast conversation format');
        }
        if (needsTranslationLanguage(formData.conversationalLanguageMode) &&
            !(formData.newsSources[0] && formData.newsSources[0].translationLanguage)) {
          throw new Error(`Language mode "${formData.conversationalLanguageMode}" requires a translation language for the selected news source`);
        }
      }

      // Free plan can't remove intro/outro — force both on regardless of
      // stored state (belt-and-braces; the checkboxes are disabled+checked
      // already, but server-side enforcement doesn't land until M1).
      const baseData = {
        ...formData,
        conversationalDifficulty: formData.episodeFormat === 'conversational'
          ? (formData.conversationalDifficulty || 'medium')
          : null
      };
      const submitData = isFreePlan
        ? {
            ...baseData,
            audioSettings: { ...formData.audioSettings, includeIntro: true, includeOutro: true },
            articlesPerEpisode: 3
          }
        : baseData;

      // Create schedule
      const schedule = await scheduleService.createSchedule(submitData);

      onComplete(schedule);
    } catch (error) {
      if (error.waitlisted) {
        // Structured 403 (§9, free-tier-cap-enforcement plan): the conversion moment, not a
        // generic failure — open the pricing modal with waitlist copy instead of an error toast.
        setError(error.waitlistMessage || "You're on the free-tier waitlist — upgrade to Plus to skip the line.");
        openPricingModal();
      } else {
        setError(error.message);
      }
      setIsLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateAudioSettings = (field, value) => {
    setFormData(prev => ({
      ...prev,
      audioSettings: {
        ...prev.audioSettings,
        [field]: value
      }
    }));
  };

  const updateEpisodeFormat = (format) => {
    setFormData(prev => ({
      ...prev,
      episodeFormat: format,
      // Switching to article clears the mode; switching to conversational starts unselected
      // so the user must make an explicit choice (mirrors the backend requirement).
      conversationalLanguageMode: format === 'conversational' ? prev.conversationalLanguageMode : null,
      conversationalDifficulty: format === 'conversational' ? (prev.conversationalDifficulty || 'medium') : null,
      hostVoices: format === 'conversational' ? prev.hostVoices : null
    }));
  };

  const updateConversationalMode = (mode) => {
    updateFormData('conversationalLanguageMode', mode);
  };

  const setSourceTranslationLanguage = (languageCode) => {
    const translationVoiceId = languageCode ? getDefaultPollyVoiceForLanguage(languageCode) : null;
    setFormData(prev => ({
      ...prev,
      newsSources: prev.newsSources.map((source, idx) =>
        idx === 0 ? { ...source, translationLanguage: languageCode || null, translationVoiceId } : source
      )
    }));
  };

  const toggleRefreshTime = (index) => {
    setFormData(prev => ({
      ...prev,
      refreshTimes: prev.refreshTimes.map((time, idx) =>
        idx === index ? { ...time, enabled: !time.enabled } : time
      )
    }));
  };

  const updateRefreshTime = (index, localTime) => {
    setFormData(prev => ({
      ...prev,
      refreshTimes: prev.refreshTimes.map((time, idx) =>
        idx === index ? { ...time, localTime } : time
      )
    }));
  };

  const addRefreshTime = () => {
    if (formData.refreshTimes.length >= maxAddableRefreshTimes) {
      alert(dailyRunsMessage);
      return;
    }
    setFormData(prev => ({
      ...prev,
      refreshTimes: [...prev.refreshTimes, { localTime: '20:00', enabled: true }]
    }));
  };

  const removeRefreshTime = (index) => {
    if (formData.refreshTimes.length <= 1) {
      alert('At least one refresh time is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      refreshTimes: prev.refreshTimes.filter((_, idx) => idx !== index)
    }));
  };

  const toggleSource = (source) => {
    const isSelected = formData.newsSources.some(s => s.url === source.url);
    
    if (isSelected) {
      // Deselect the source
      setFormData(prev => ({
        ...prev,
        newsSources: []
      }));
    } else {
      // Select only this source (replace any previously selected)
      setFormData(prev => ({
        ...prev,
        newsSources: [source]
      }));
    }
  };

  // Episode style validity (mirrors backend validateScheduleConfig rules)
  const selectedSource = formData.newsSources[0];
  const requiresTranslationLanguage = formData.episodeFormat === 'conversational' &&
    needsTranslationLanguage(formData.conversationalLanguageMode);
  const hasTranslationLanguage = !!(selectedSource && selectedSource.translationLanguage);
  const episodeStyleValid = formData.episodeFormat === 'article' ||
    (!!formData.conversationalLanguageMode && (!requiresTranslationLanguage || hasTranslationLanguage));

  const renderStep1 = () => (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0', color: '#2d3748' }}>📋 Basic Information</h3>
      
      {/* Schedule Name */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Schedule Name *
        </label>
        <input
          type="text"
          value={formData.scheduleName}
          onChange={(e) => updateFormData('scheduleName', e.target.value)}
          placeholder="e.g., Morning News Briefing"
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '6px',
            border: '1px solid #cbd5e0',
            fontSize: '1rem'
          }}
        />
        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
          Give your schedule a descriptive name
        </div>
      </div>

      {/* Timezone */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Timezone *
        </label>
        <TimezoneSelector
          value={formData.timezone}
          onChange={(tz) => updateFormData('timezone', tz)}
        />
        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>
          All refresh times will use this timezone
        </div>
      </div>

      {/* Refresh Times */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Refresh Times * (1-2 per day)
        </label>
        {formData.refreshTimes.map((time, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={time.enabled}
              onChange={() => toggleRefreshTime(idx)}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <input
              type="time"
              value={time.localTime}
              onChange={(e) => updateRefreshTime(idx, e.target.value)}
              style={{
                flex: 1,
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #cbd5e0',
                fontSize: '1rem'
              }}
            />
            {formData.refreshTimes.length > 1 && (
              <button
                onClick={() => removeRefreshTime(idx)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid #fc8181',
                  background: 'white',
                  color: '#c53030',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {formData.refreshTimes.length < maxAddableRefreshTimes && (
          <button
            onClick={addRefreshTime}
            className="btn btn-secondary btn-sm"
            style={{ marginTop: '0.5rem' }}
          >
            + Add Another Time
          </button>
        )}
        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
          Podcast will be generated at these times each day
        </div>
        {dailyRunLimitReached && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem 1rem',
            background: '#fff3cd',
            border: '1px solid #F9C500',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '0.85rem'
          }}>
            ⚠️ {dailyRunsMessage}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0', color: '#2d3748' }}>📰 Select News Source</h3>
      
      {userSources.length === 0 ? (
        <div style={{ padding: '2rem', background: '#fffbeb', borderRadius: '8px', textAlign: 'center', border: '1px solid #F9C500' }}>
          <p style={{ margin: '0 0 1rem 0', color: '#92400e' }}>
            ⚠️ No news sources configured yet
          </p>
          <p style={{ margin: '0', fontSize: '0.9rem', color: '#92400e' }}>
            Please add news sources in the "News Sources" tab first
          </p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '0.9rem', color: '#718096', marginBottom: '1rem' }}>
            {formData.newsSources.length === 0 ? (
              'Select a news source for this schedule'
            ) : (
              <>Selected: <strong>{formData.newsSources[0].name}</strong></>
            )}
          </div>
          
          <div className="source-selection-list">
            {userSources.map((source, idx) => {
              const isSelected = formData.newsSources.some(s => s.url === source.url);

              return (
                <div
                  key={idx}
                  className="source-checkbox-item"
                  onClick={() => toggleSource(source)}
                  style={{
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    border: `2px solid ${isSelected ? 'var(--color-primary)' : '#e2e8f0'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: isSelected ? 'var(--color-sky-light)' : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: isSelected ? 'var(--color-primary)' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      {isSelected && '✓'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#2d3748', marginBottom: '0.25rem' }}>
                        {source.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                        {source.url}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '0.25rem' }}>
                        {source.sourceLanguage} • {source.extractionType}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0', color: '#2d3748' }}>🎙️ Audio Settings</h3>

      {/* Episode Style (Phase 1, conversational-podcast plan) */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Episode Style
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              name="episodeFormat"
              checked={formData.episodeFormat === 'article'}
              onChange={() => updateEpisodeFormat('article')}
              style={{ width: '18px', height: '18px' }}
            />
            <span>📖 Read the news</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              name="episodeFormat"
              checked={formData.episodeFormat === 'conversational'}
              onChange={() => updateEpisodeFormat('conversational')}
              style={{ width: '18px', height: '18px' }}
            />
            <span>🗣️ Podcast conversation</span>
          </label>
        </div>

        {formData.episodeFormat === 'conversational' && (
          <div style={{ marginTop: '1rem', paddingLeft: '1.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Language Mode
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {CONVERSATIONAL_LANGUAGE_MODES.map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="conversationalLanguageMode"
                    checked={formData.conversationalLanguageMode === opt.value}
                    onChange={() => updateConversationalMode(opt.value)}
                    style={{ width: '18px', height: '18px', marginTop: '0.15rem' }}
                  />
                  <span>
                    <div>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#718096' }}>{opt.description}</div>
                  </span>
                </label>
              ))}
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
                Difficulty
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {CONVERSATIONAL_DIFFICULTIES.map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="conversationalDifficulty"
                      checked={(formData.conversationalDifficulty || 'medium') === opt.value}
                      onChange={() => updateFormData('conversationalDifficulty', opt.value)}
                      style={{ width: '18px', height: '18px', marginTop: '0.15rem' }}
                    />
                    <span>
                      <div>{opt.label}</div>
                      <div style={{ fontSize: '0.8rem', color: '#718096' }}>{opt.description}</div>
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '0.25rem' }}>
                Difficulty shapes the English dialogue; translated lines follow from it.
              </div>
            </div>

            {formData.conversationalLanguageMode && (
              <HostVoicePicker
                value={formData.hostVoices}
                onChange={(hv) => updateFormData('hostVoices', hv)}
                secondaryLanguage={formData.newsSources?.[0]?.translationLanguage || null}
                needsSecondary={needsTranslationLanguage(formData.conversationalLanguageMode)}
                languageMode={formData.conversationalLanguageMode}
              />
            )}

            {requiresTranslationLanguage && !hasTranslationLanguage && (
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#718096' }}>
                  This mode needs a translation language for your selected news source:
                </label>
                <select
                  value=""
                  onChange={(e) => setSourceTranslationLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e0',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Choose a language...</option>
                  {POLLY_STANDARD_LANGUAGE_OPTIONS.map(opt => (
                    <option key={opt.languageCode} value={opt.languageCode}>{opt.languageName}</option>
                  ))}
                </select>
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: '#fff3cd',
                  border: '1px solid #F9C500',
                  borderRadius: '6px',
                  color: '#92400e',
                  fontSize: '0.85rem'
                }}>
                  ⚠️ Select a translation language above before you can continue.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Articles per episode (Topic 5, monetization plan) — plan-aware, server-enforced;
          this control is UI-convenience only, the backend rejects out-of-range values. */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Articles per episode
          {articlesLocked && <span className="plan-lock-hint">🔒 Plus</span>}
        </label>
        <select
          value={articlesLocked ? 3 : formData.articlesPerEpisode}
          disabled={articlesLocked}
          onChange={(e) => updateFormData('articlesPerEpisode', parseInt(e.target.value, 10))}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '6px',
            border: '1px solid #cbd5e0',
            fontSize: '1rem',
            background: articlesLocked ? '#f7fafc' : 'white',
            cursor: articlesLocked ? 'not-allowed' : 'pointer',
            color: articlesLocked ? '#718096' : 'inherit'
          }}
        >
          {Array.from({ length: planLimits.maxArticlesPerSource }, (_, i) => i + 1).map(n => (
            <option key={n} value={n}>{n} article{n === 1 ? '' : 's'}</option>
          ))}
        </select>
        <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
          {articlesLocked ? (
            <>
              Free plan uses 3 articles per episode.{' '}
              <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
                Upgrade to Plus
              </button>{' '}
              to choose your own count.
            </>
          ) : (
            'Fewer articles make shorter, more focused episodes. More articles give broader coverage and may create longer episodes.'
          )}
        </div>
      </div>

      <Dismissible id="wizard-voice-note" title="Voice and language settings note" userSub={user?.username}>
        <div className="dismissible__reserve-right" style={{
          padding: '1rem',
          backgroundColor: 'var(--color-sky-light)',
          borderLeft: '4px solid var(--color-primary)',
          borderRadius: '4px',
          marginBottom: '1.5rem',
          fontSize: '0.9rem',
          color: '#2d3748'
        }}>
          <strong>ℹ️ Note:</strong> Voice and language settings are configured per news source.
          These audio settings apply to the overall podcast output.
        </div>
      </Dismissible>

      {/* Audio Speed */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
          Audio Speed: {formData.audioSettings.audioSpeed}x
        </label>
        <input
          type="range"
          min="0.5"
          max="2.0"
          step="0.1"
          value={formData.audioSettings.audioSpeed}
          onChange={(e) => updateAudioSettings('audioSpeed', e.target.value)}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#718096' }}>
          <span>0.5x (Slower)</span>
          <span>1.0x (Normal)</span>
          <span>2.0x (Faster)</span>
        </div>
      </div>

      {/* Intro/Outro */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: isFreePlan ? 'not-allowed' : 'pointer' }}>
          <input
            type="checkbox"
            checked={isFreePlan ? true : formData.audioSettings.includeIntro}
            disabled={isFreePlan}
            onChange={(e) => updateAudioSettings('includeIntro', e.target.checked)}
            style={{ width: '20px', height: '20px' }}
          />
          <span style={{ fontWeight: '500', color: '#4a5568' }}>Include Intro</span>
          {isFreePlan && <span className="plan-lock-hint">🔒 Plus</span>}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isFreePlan ? 'not-allowed' : 'pointer' }}>
          <input
            type="checkbox"
            checked={isFreePlan ? true : formData.audioSettings.includeOutro}
            disabled={isFreePlan}
            onChange={(e) => updateAudioSettings('includeOutro', e.target.checked)}
            style={{ width: '20px', height: '20px' }}
          />
          <span style={{ fontWeight: '500', color: '#4a5568' }}>Include Outro</span>
          {isFreePlan && <span className="plan-lock-hint">🔒 Plus</span>}
        </label>
        {isFreePlan && (
          <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
            Intro and outro are included for all episodes on the free plan.{' '}
            <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
              Upgrade to Plus
            </button>{' '}
            to remove them.
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div>
      <h3 style={{ margin: '0 0 1.5rem 0', color: '#2d3748' }}>✅ Review & Create</h3>
      
      <div style={{ background: '#f7fafc', padding: '1.5rem', borderRadius: '8px' }}>
        {/* Basic Info */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#4a5568', fontSize: '1rem' }}>Basic Information</h4>
          <div style={{ fontSize: '0.9rem', color: '#2d3748', lineHeight: '1.6' }}>
            <div><strong>Name:</strong> {formData.scheduleName}</div>
            <div><strong>Timezone:</strong> {formData.timezone}</div>
            <div>
              <strong>Refresh Times:</strong>{' '}
              {formData.refreshTimes
                .filter(t => t.enabled)
                .map(t => t.localTime)
                .join(', ')}
            </div>
          </div>
        </div>

        {/* News Sources */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#4a5568', fontSize: '1rem' }}>
            News Source (1)
          </h4>
          <div style={{ fontSize: '0.9rem', color: '#2d3748' }}>
            {formData.newsSources.map((source, idx) => (
              <div key={idx} style={{ marginBottom: '0.25rem' }}>• {source.name}</div>
            ))}
          </div>
        </div>

        {/* Episode Style */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#4a5568', fontSize: '1rem' }}>Episode Style</h4>
          <div style={{ fontSize: '0.9rem', color: '#2d3748', lineHeight: '1.6' }}>
            {formData.episodeFormat === 'conversational' ? (
              <div>
                <strong>Podcast conversation</strong> —{' '}
                {CONVERSATIONAL_LANGUAGE_MODES.find(m => m.value === formData.conversationalLanguageMode)?.label
                  || 'no language mode selected'}
                {hasTranslationLanguage && ` (${getPollyLanguageName(selectedSource.translationLanguage)})`}
                {' · '}
                {CONVERSATIONAL_DIFFICULTIES.find(d => d.value === (formData.conversationalDifficulty || 'medium'))?.label}
              </div>
            ) : (
              <div><strong>Read the news</strong></div>
            )}
          </div>
        </div>

        {/* Audio Settings */}
        <div>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#4a5568', fontSize: '1rem' }}>Audio Settings</h4>
          <div style={{ fontSize: '0.9rem', color: '#2d3748', lineHeight: '1.6' }}>
            <div><strong>Speed:</strong> {formData.audioSettings.audioSpeed}x</div>
            <div>
              <strong>Intro/Outro:</strong>{' '}
              {formData.audioSettings.includeIntro ? '✅' : '❌'} / {formData.audioSettings.includeOutro ? '✅' : '❌'}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          background: '#fff3cd',
          border: '1px solid #F9C500',
          borderRadius: '6px',
          color: '#92400e'
        }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );

  return (
    <div className="wizard-overlay">
      <div className="wizard-modal">
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#2d3748' }}>Create New Schedule</h2>
          <button
            onClick={onCancel}
            disabled={isLoading}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.5rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              color: '#718096'
            }}
          >
            ✕
          </button>
        </div>

        {/* Progress */}
        <div style={{ padding: '1rem 1.5rem', background: '#f7fafc' }}>
          <div className="wizard-step-indicator">
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                className="wizard-step"
                style={{
                  flex: 1,
                  height: '4px',
                  background: s <= step ? 'var(--color-primary)' : '#e2e8f0',
                  marginRight: s < 4 ? '0.5rem' : 0,
                  borderRadius: '2px'
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#718096' }}>
            Step {step} of 4
          </div>
        </div>

        {/* Content */}
        <div className="wizard-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="wizard-navigation">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onCancel()}
            disabled={isLoading}
            className="btn btn-secondary"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={isLoading || (step === 1 && !formData.scheduleName.trim()) || (step === 1 && wouldExceedDailyRuns) || (step === 2 && formData.newsSources.length === 0)}
              className="btn btn-primary"
              style={(isLoading || (step === 1 && !formData.scheduleName.trim()) || (step === 1 && wouldExceedDailyRuns) || (step === 2 && formData.newsSources.length === 0) || (step === 3 && !episodeStyleValid))
                ? { background: '#a0aec0', cursor: 'not-allowed', opacity: 0.6 }
                : undefined}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading || wouldExceedDailyRuns}
              className="btn btn-primary"
            >
              {isLoading ? 'Creating...' : '✓ Create Schedule'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScheduleWizard;
